import React, { useState } from "react";
import styled from "styled-components";
import DataTable from "../components/DataTable";
import apiClient from "../utils/apiClient";
import { Helmet } from "react-helmet-async";
import PageTitle from "../components/PageTitle";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";

const Content = styled.div`
  width: auto;
  flex: 1;
  padding: 40px 32px;
  margin-inline-start: 30px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 8px;
`;

const HeadTabMenu = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const S2SManage = () => {
  const { t } = useTranslation();

  const [filter, setFilter] = useState("from_me");
  const [selectedRows, setSelectedRows] = useState([]);
  const [refreshTable, setRefreshTable] = useState(0);
  const [loading, setLoading] = useState(false);

  const apiPaths = {
    from_me: "/s2s/active/from_me",
    to_me: "/s2s/active/to_me",
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setSelectedRows([]);
  };

  const handleSelectionChange = (selection) => {
    setSelectedRows(selection);
  };

  const handleRefresh = () => {
    setRefreshTable((prev) => prev + 1);
  };

  const handleRevoke = async () => {
    if (selectedRows.length === 0) {
      alert(t("s2sManage.validation.selectClientsToRevoke"));
      return;
    }

    try {
      setLoading(true);

      const grouped = selectedRows.reduce((acc, item) => {
        const [delegationId, clientId] = item.split("_");

        if (!acc[delegationId]) {
          acc[delegationId] = [];
        }

        acc[delegationId].push(clientId);
        return acc;
      }, {});

      if (filter === "from_me") {
        await Promise.all(
          Object.entries(grouped).map(([delegationId, clientIds]) =>
            apiClient.post("/s2s/assignments/revoke", {
              delegation_id: delegationId,
              clients: clientIds,
            }),
          ),
        );

        alert(t("s2sManage.revokeRequestSent"));
      } else {
        await Promise.all(
          Object.entries(grouped).map(([delegationId, clientIds]) =>
            apiClient.post("/s2s/revoke_immediate", {
              delegation_id: delegationId,
              clients: clientIds,
            }),
          ),
        );

        alert(t("s2sManage.delegationCancelled"));
      }

      setRefreshTable((prev) => prev + 1);
      setSelectedRows([]);
    } catch (error) {
      console.error("Ошибка отзыва делегирования:", error);
      alert(t("s2sManage.revokeError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Content>
      <Helmet>
        <title>{t("s2sManage.metaTitle")}</title>
        <meta name="description" content={t("s2sManage.metaDescription")} />
      </Helmet>

      <PageTitle>{t("s2sManage.title")}</PageTitle>

      <HeadTabMenu>
        <Button
          variant={filter === "from_me" ? "contained" : "outlined"}
          color="primary"
          onClick={() => handleFilterChange("from_me")}
        >
          {t("s2sManage.fromMe")}
        </Button>

        <Button
          variant={filter === "to_me" ? "contained" : "outlined"}
          color="primary"
          onClick={() => handleFilterChange("to_me")}
        >
          {t("s2sManage.toMe")}
        </Button>

        <Button variant="outlined" color="secondary" onClick={handleRefresh}>
          {t("s2sManage.refresh")}
        </Button>
      </HeadTabMenu>

      <DataTable
        path={apiPaths[filter]}
        columns={[
          {
            field: "client_id",
            headerName: t("s2sManage.columns.clientId"),
            flex: 1,
          },
          { field: "fio", headerName: t("s2sManage.columns.fio"), flex: 1 },
          {
            field: "account_number",
            headerName: t("s2sManage.columns.account"),
            flex: 1,
          },
          {
            field: "from_fio",
            headerName: t("s2sManage.columns.from"),
            flex: 1,
          },
          { field: "to_fio", headerName: t("s2sManage.columns.to"), flex: 1 },
          {
            field: "is_active",
            headerName: t("s2sManage.columns.active"),
            flex: 1,
            renderCell: (params) =>
              params.row.is_active ? t("s2sManage.yes") : t("s2sManage.no"),
          },
        ]}
        checkboxSelection={true}
        onSelectionChange={handleSelectionChange}
        refresh={refreshTable}
      />

      {selectedRows.length > 0 && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleRevoke}
          disabled={loading}
        >
          {filter === "from_me"
            ? t("s2sManage.requestRevoke")
            : t("s2sManage.cancelDelegation")}
        </Button>
      )}
    </Content>
  );
};

export default S2SManage;
